import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Separater API-Client für Super-Admin (ohne normale Auth-Interceptoren)
const superAdminClient = axios.create({
  baseURL: API_URL
});

// Token aus localStorage holen
const getToken = () => localStorage.getItem('superAdminToken');

// Request Interceptor für Super-Admin Token
superAdminClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor für Fehlerbehandlung
superAdminClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('superAdminToken');
      window.location.href = '/super-admin/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Super-Admin Login (unterstützt 2FA)
 */
export const login = async (username, passwort, totpCode = null) => {
  const response = await superAdminClient.post('/super-admin/login', {
    username,
    passwort,
    totpCode
  });
  return response.data;
};

/**
 * 2FA-Verifizierung (zweiter Schritt des Logins)
 */
export const verify2FA = async (tempToken, totpCode) => {
  const response = await superAdminClient.post('/super-admin/verify-2fa', {
    tempToken,
    totpCode
  });
  return response.data;
};

/**
 * 3FA-Verifizierung (dritter Schritt des Logins: Email-OTP)
 */
export const verify3FA = async (tempToken, emailCode) => {
  const response = await superAdminClient.post('/super-admin/verify-3fa', {
    tempToken,
    emailCode
  });
  return response.data;
};

/**
 * 2FA-Status abrufen
 */
export const get2FAStatus = async () => {
  const response = await superAdminClient.get('/super-admin/2fa/status');
  return response.data;
};

/**
 * 2FA-Setup starten (generiert QR-Code)
 */
export const setup2FA = async () => {
  const response = await superAdminClient.post('/super-admin/2fa/setup');
  return response.data;
};

/**
 * 2FA-Setup verifizieren (aktiviert 2FA)
 */
export const verifySetup2FA = async (totpCode) => {
  const response = await superAdminClient.post('/super-admin/2fa/verify-setup', {
    totpCode
  });
  return response.data;
};

/**
 * 2FA deaktivieren
 */
export const disable2FA = async (passwort, totpCode) => {
  const response = await superAdminClient.post('/super-admin/2fa/disable', {
    passwort,
    totpCode
  });
  return response.data;
};

// =========================================
// 3FA MANAGEMENT
// =========================================

/**
 * 3FA-Status abrufen
 */
export const get3FAStatus = async () => {
  const response = await superAdminClient.get('/super-admin/3fa/status');
  return response.data;
};

/**
 * 3FA-Setup starten (sendet Test-OTP an Email)
 */
export const setup3FA = async (email) => {
  const response = await superAdminClient.post('/super-admin/3fa/setup', { email });
  return response.data;
};

/**
 * 3FA-Setup bestätigen
 */
export const verifySetup3FA = async (code) => {
  const response = await superAdminClient.post('/super-admin/3fa/verify-setup', { code });
  return response.data;
};

/**
 * 3FA deaktivieren
 */
export const disable3FA = async (passwort) => {
  const response = await superAdminClient.post('/super-admin/3fa/disable', { passwort });
  return response.data;
};

/**
 * Eigenes Passwort ändern
 */
export const changePassword = async (currentPassword, newPassword) => {
  const response = await superAdminClient.post('/super-admin/change-password', {
    currentPassword,
    newPassword
  });
  return response.data;
};

/**
 * Super-Admin Session prüfen
 */
export const getMe = async () => {
  const response = await superAdminClient.get('/super-admin/me');
  return response.data;
};

/**
 * Dashboard Statistiken
 */
export const getStats = async () => {
  const response = await superAdminClient.get('/super-admin/stats');
  return response.data;
};

/**
 * Alle Organisationen auflisten
 */
export const getOrganisations = async (params = {}) => {
  const response = await superAdminClient.get('/super-admin/organisations', { params });
  return response.data;
};

/**
 * Organisation Details
 */
export const getOrganisationDetails = async (id) => {
  const response = await superAdminClient.get(`/super-admin/organisations/${id}`);
  return response.data;
};

/**
 * Als Organisation Admin einloggen (Impersonate)
 */
export const impersonate = async (organisationId) => {
  const response = await superAdminClient.post(`/super-admin/impersonate/${organisationId}`);
  return response.data;
};

/**
 * Agent manuell synchronisieren (HEYKIKI)
 */
export const resyncAgent = async (organisationId) => {
  const response = await superAdminClient.post(`/super-admin/organisations/${organisationId}/resync-agent`);
  return response.data;
};

/**
 * Organisation bearbeiten (Name, Agent-ID, Login-Email)
 */
export const updateOrg = async (organisationId, data) => {
  const response = await superAdminClient.patch(`/super-admin/organisations/${organisationId}`, data);
  return response.data;
};

/**
 * HEYKIKI: Aktuellen + auto-generierten ElevenLabs-System-Prompt einer Sub-Org laden.
 * Liefert auch den Lock-State (manualPromptLocked) und Audit-Felder.
 */
export const getAgentPrompt = async (organisationId) => {
  const response = await superAdminClient.get(`/super-admin/organisations/${organisationId}/agent-prompt`);
  return response.data;
};

/**
 * HEYKIKI: Manuellen System-Prompt setzen oder löschen.
 * `manualSystemPrompt = null` oder leerer String → Reset auf Auto-Generierung.
 * Triggert anschließend einen synchronen ElevenLabs-Agent-Sync.
 */
export const setAgentPrompt = async (organisationId, { manualSystemPrompt, manualFirstMessage } = {}) => {
  const response = await superAdminClient.put(
    `/super-admin/organisations/${organisationId}/agent-prompt`,
    { manualSystemPrompt, manualFirstMessage }
  );
  return response.data;
};

/**
 * HEYKIKI: Kiki-Zentrale für eine Sub-Org sperren oder entsperren.
 * Bei `locked=true` erhalten Org-Admin-PUTs in der Kiki-Zentrale 423 Locked.
 */
export const setKikiZentraleLock = async (organisationId, locked) => {
  const response = await superAdminClient.put(
    `/super-admin/organisations/${organisationId}/kiki-zentrale-lock`,
    { locked }
  );
  return response.data;
};

/**
 * Letzte Anrufe einer Org abrufen
 */
export const getRecentCalls = async (organisationId, limit = 10) => {
  const response = await superAdminClient.get(`/super-admin/organisations/${organisationId}/recent-calls`, { params: { limit } });
  return response.data;
};

/**
 * Organisation deaktivieren
 */
export const deactivateOrg = async (organisationId) => {
  const response = await superAdminClient.post(`/super-admin/organisations/${organisationId}/deactivate`);
  return response.data;
};

/**
 * Organisation reaktivieren
 */
export const reactivateOrg = async (organisationId) => {
  const response = await superAdminClient.post(`/super-admin/organisations/${organisationId}/reactivate`);
  return response.data;
};

/**
 * Passwort eines Org-Admins zurücksetzen
 */
export const resetOrgPassword = async (organisationId, newPassword) => {
  const response = await superAdminClient.post(`/super-admin/organisations/${organisationId}/reset-password`, { newPassword });
  return response.data;
};

/**
 * Impersonate beenden und zurück zum Super-Admin Panel
 */
export const endImpersonate = async () => {
  // Hier brauchen wir den aktuellen (Impersonate-)Token
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${API_URL}/super-admin/end-impersonate`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

/**
 * Audit Logs abrufen
 */
export const getAuditLogs = async (params = {}) => {
  const response = await superAdminClient.get('/super-admin/audit-logs', { params });
  return response.data;
};

/**
 * Revenue/MRR Statistiken von Stripe
 */
export const getRevenueStats = async (params = {}) => {
  const response = await superAdminClient.get('/super-admin/revenue', { params });
  return response.data;
};

/**
 * Refunds abrufen
 */
export const getRefunds = async (params = {}) => {
  const response = await superAdminClient.get('/super-admin/refunds', { params });
  return response.data;
};

/**
 * Refunds von Stripe synchronisieren
 */
export const syncRefunds = async () => {
  const response = await superAdminClient.post('/super-admin/refunds/sync');
  return response.data;
};

/**
 * Kosten für einen Monat abrufen
 */
export const getCosts = async (year, month) => {
  const response = await superAdminClient.get('/super-admin/costs', {
    params: { year, month }
  });
  return response.data;
};

/**
 * Manuelle Kosten speichern
 */
export const saveCosts = async (data) => {
  const response = await superAdminClient.post('/super-admin/costs', data);
  return response.data;
};

/**
 * Kosten-Historie (12 Monate)
 */
export const getCostsHistory = async () => {
  const response = await superAdminClient.get('/super-admin/costs/history');
  return response.data;
};

// =========================================
// BRANCHEN-ICONS MANAGEMENT
// =========================================

/**
 * Alle Branchen-Icons abrufen
 */
export const getBrancheIcons = async () => {
  const response = await superAdminClient.get('/super-admin/branche-icons');
  return response.data;
};

/**
 * Branchen-Icon hochladen
 */
export const uploadBrancheIcon = async (brancheCode, file) => {
  const formData = new FormData();
  formData.append('icon', file);

  const response = await superAdminClient.post(
    `/super-admin/branche-icons/${brancheCode}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

/**
 * Branchen-Icon löschen
 */
export const deleteBrancheIcon = async (brancheCode) => {
  const response = await superAdminClient.delete(`/super-admin/branche-icons/${brancheCode}`);
  return response.data;
};

// =========================================
// GLOBALES CHAT-ICON MANAGEMENT
// =========================================

/**
 * Globales Chat-Icon abrufen
 */
export const getChatIcon = async () => {
  const response = await superAdminClient.get('/super-admin/chat-icon');
  return response.data;
};

/**
 * Globales Chat-Icon hochladen
 */
export const uploadChatIcon = async (file) => {
  const formData = new FormData();
  formData.append('chatIcon', file);

  const response = await superAdminClient.post(
    '/super-admin/chat-icon',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

/**
 * Globales Chat-Icon löschen
 */
export const deleteChatIcon = async () => {
  const response = await superAdminClient.delete('/super-admin/chat-icon');
  return response.data;
};

// =========================================
// WHATSAPP PLATTFORM (WABA)
// =========================================

/**
 * WhatsApp Plattform-Status (WABA) abrufen
 */
export const getWhatsAppPlatformStatus = async () => {
  const response = await superAdminClient.get('/super-admin/whatsapp-platform');
  return response.data;
};

/**
 * WhatsApp WABA-Status forciert prüfen
 */
export const checkWhatsAppWaba = async () => {
  const response = await superAdminClient.post('/super-admin/whatsapp-check-waba');
  return response.data;
};

export default {
  login,
  verify2FA,
  verify3FA,
  get2FAStatus,
  setup2FA,
  verifySetup2FA,
  disable2FA,
  get3FAStatus,
  setup3FA,
  verifySetup3FA,
  disable3FA,
  getMe,
  getStats,
  getOrganisations,
  getOrganisationDetails,
  impersonate,
  resyncAgent,
  endImpersonate,
  getAuditLogs,
  getRevenueStats,
  getRefunds,
  getCosts,
  saveCosts,
  getCostsHistory,
  getBrancheIcons,
  uploadBrancheIcon,
  deleteBrancheIcon,
  getChatIcon,
  uploadChatIcon,
  deleteChatIcon,
  getWhatsAppPlatformStatus,
  checkWhatsAppWaba
};
